import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { personalTodosService } from '../../lib/personal-todos-service';
import { taskBig3Integration } from '../../lib/task-big3-integration';
import { tasksService } from '../../lib/tasks-service';
import { format, isToday, isSameDay, parseISO, addDays, isAfter, setHours, setMinutes } from 'date-fns';
import { 
  Target, 
  Plus, 
  X, 
  CheckCircle, 
  Circle, 
  Calendar, 
  Sunrise, 
  Sun, 
  Moon as MoonIcon, 
  Zap, 
  Battery, 
  Coffee,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Mail,
  BookmarkPlus,
  Waves,
  Bike,
  Gamepad2,
  Heart,
  Book,
  Save,
  Check,
  Clock,
  EyeOff,
  ListTodo,
  Star
} from 'lucide-react';
import debounce from '../../utils/debounce';

// Use unified responsive system instead of custom hook
import { useRoleAwareLayout } from '../../hooks/responsive/useResponsive';

// Time slot configuration
const TIME_SLOTS = [
  { 
    id: 'intention1', 
    label: 'Intention 1', 
    icon: Sunrise,
    gradient: 'bg-gradient-to-br from-amber-400 to-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700'
  },
  { 
    id: 'intention2', 
    label: 'Intention 2', 
    icon: Sun,
    gradient: 'bg-gradient-to-br from-azure-400 to-azure-600',
    bgColor: 'bg-azure-50',
    borderColor: 'border-azure-200',
    textColor: 'text-azure-700'
  },
  { 
    id: 'intention3', 
    label: 'Intention 3', 
    icon: MoonIcon,
    gradient: 'bg-gradient-to-br from-violet-400 to-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    textColor: 'text-violet-700'
  }
];

// Energy levels configuration
const ENERGY_LEVELS = [
  { 
    id: 'low', 
    label: 'Chill', 
    icon: Coffee,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    borderColor: 'border-primary-500',
    selectedBg: 'bg-primary-500',
    hoverBg: 'hover:bg-primary-50'
  },
  { 
    id: 'medium', 
    label: 'Normal', 
    icon: BookOpen,
    color: 'text-gold-600',
    bgColor: 'bg-gold-50',
    borderColor: 'border-gold-500',
    selectedBg: 'bg-gold-500',
    hoverBg: 'hover:bg-gold-50'
  },
  { 
    id: 'high', 
    label: 'Energetic', 
    icon: Zap,
    color: 'text-coral-600',
    bgColor: 'bg-coral-50',
    borderColor: 'border-coral-500',
    selectedBg: 'bg-coral-500',
    hoverBg: 'hover:bg-coral-50'
  }
];

// Helper functions for data filtering
const getTodayCalendarEvents = (familyCalendar, selectedDate, currentUserId = null) => {
  if (!familyCalendar || !Array.isArray(familyCalendar)) return [];
  
  return familyCalendar
    .filter(event => {
      // Filter by date first
      let isToday = false;
      
      // Handle start_time/end_time format (from QuickWorkSchedule)
      if (event.start_time) {
        try {
          const eventDate = parseISO(event.start_time);
          isToday = isSameDay(eventDate, selectedDate);
        } catch {
          return false;
        }
      }
      // Handle both dateTime and separate date field formats
      else if (event.dateTime) {
        try {
          const eventDate = parseISO(event.dateTime);
          isToday = isSameDay(eventDate, selectedDate);
        } catch {
          return false;
        }
      } else if (event.date) {
        try {
          // Handle separate date field (from QuickAdd events)
          const eventDate = typeof event.date === 'string' ? parseISO(event.date) : event.date;
          isToday = isSameDay(eventDate, selectedDate);
        } catch {
          return false;
        }
      }
      
      if (!isToday) return false;
      
      // Filter work shifts: only show work shifts assigned to current user
      const isWorkShift = event.category === 'work' || event.activityType === 'work-shift';
      if (isWorkShift && currentUserId) {
        // Work shifts should only be visible to the person they're assigned to
        return event.kidId === currentUserId || event.createdBy === currentUserId;
      }
      
      // Show all other events to everyone
      return true;
    })
    .map(event => {
      const isWorkShift = event.category === 'work' || event.activityType === 'work-shift';
      
      let timeDisplay;
      if (event.start_time && event.end_time) {
        const startTime = format(parseISO(event.start_time), 'h:mm a');
        const endTime = format(parseISO(event.end_time), 'h:mm a');
        timeDisplay = `${startTime} - ${endTime}`;
      } else if (event.dateTime) {
        timeDisplay = format(parseISO(event.dateTime), 'h:mm a');
      } else {
        timeDisplay = event.time || 'All day';
      }

      return {
        id: event.id,
        title: isWorkShift ? `🏢 ${event.title}` : event.title,
        time: timeDisplay,
        category: isWorkShift ? 'Work Shift' : (event.category || 'Event'),
        type: 'calendar',
        isWorkShift,
        priority: isWorkShift ? 'high' : 'normal'
      };
    });
};

const getTodayTodos = async (userId, selectedDate) => {
  try {
    const todayDateString = format(selectedDate, 'yyyy-MM-dd');
    const today = new Date(todayDateString);
    
    // Get todos due today
    const todayTodos = await personalTodosService.getTodos(userId, {
      status: ['pending', 'in-progress'],
      dueDate: todayDateString
    });
    
    // Get overdue todos
    const overdueTodos = await personalTodosService.getTodos(userId, {
      status: ['pending', 'in-progress'],
      overdue: true
    });
    
    // Combine and deduplicate
    const allTodos = [...todayTodos, ...overdueTodos];
    const uniqueTodos = allTodos.filter((todo, index, self) => 
      index === self.findIndex(t => t.id === todo.id)
    );
    
    return uniqueTodos.map(todo => ({
      id: todo.id,
      title: todo.title,
      category: todo.category || 'Personal Task',
      type: 'todo',
      priority: todo.priority || 'normal',
      dueDate: todo.due_date,
      isOverdue: todo.due_date && new Date(todo.due_date) < today,
      estimatedTime: todo.estimated_minutes ? `${todo.estimated_minutes}min` : null
    }));
  } catch (error) {
    // Don't spam console with database schema errors
    if (error?.code === 'PGRST200' && error?.message?.includes('personal_todo_categories')) {
      return [];
    }
    console.error('Failed to load todos:', error);
    return [];
  }
};

// Helper function to load dismissed suggestions from localStorage
const loadDismissedSuggestions = (userId) => {
  try {
    const dismissed = localStorage.getItem(`big3-dismissed-${userId}`);
    return dismissed ? new Set(JSON.parse(dismissed)) : new Set();
  } catch (error) {
    console.error('Failed to load dismissed suggestions:', error);
    return new Set();
  }
};

// Helper function to save dismissed suggestions to localStorage
const saveDismissedSuggestions = (userId, dismissedSet) => {
  try {
    localStorage.setItem(`big3-dismissed-${userId}`, JSON.stringify([...dismissedSet]));
  } catch (error) {
    console.error('Failed to save dismissed suggestions:', error);
  }
};

// Generate energy-aware suggestions from Activities Library
const getEnergyAwareSuggestions = (timeSlot, energyLevel, userActivities = []) => {
  const suggestions = [];
  
  // Filter user's activities by energy level
  const energyMatchedActivities = userActivities.filter(activity => {
    // Handle both 'energy_level' and 'energyLevel' property names
    const activityEnergyLevel = activity.energy_level || activity.energyLevel;
    return activityEnergyLevel === energyLevel;
  });
  
  // If we have energy-matched activities from the user's library, use those
  if (energyMatchedActivities.length > 0) {
    // Take up to 4 activities to avoid overwhelming the suggestions
    const limitedActivities = energyMatchedActivities.slice(0, 4);
    
    limitedActivities.forEach((activity, index) => {
      suggestions.push({
        id: activity.id || `energy-library-${timeSlot}-${energyLevel}-${index}`,
        title: activity.title || activity.name,
        type: 'energy-suggestion',
        category: activity.category || 'Energy Match',
        energyLevel,
        timeSlot,
        description: activity.description,
        howToStart: activity.how_to_start || activity.howToStart,
        links: activity.links || [],
        icon: energyLevel === 'low' ? Coffee : energyLevel === 'medium' ? BookOpen : Zap,
        source: 'activities-library'
      });
    });
  } else {
    // Fallback to smart default suggestions if no matching activities in library
    const timeBasedSuggestions = {
      intention1: {
        low: ['coffee & journal', 'read news', 'gentle stretch', 'morning tea'],
        medium: ['exercise', 'plan day', 'email check', 'breakfast prep'],
        high: ['workout', 'run', 'power walk', 'energy boost routine']
      },
      intention2: {
        low: ['short nap', 'light snack', 'easy reading', 'listen to music'],
        medium: ['work tasks', 'errands', 'cleaning', 'organize space'],
        high: ['sport', 'gym', 'outdoor activity', 'social time']
      },
      intention3: {
        low: ['tv/movie', 'relaxing bath', 'wind down', 'gentle yoga'],
        medium: ['cook dinner', 'family time', 'creative hobbies', 'call friend'],
        high: ['evening workout', 'dance', 'project work', 'active games']
      }
    };

    const timeSuggestions = timeBasedSuggestions[timeSlot]?.[energyLevel] || [];
    
    timeSuggestions.forEach((suggestion, index) => {
      suggestions.push({
        id: `energy-default-${timeSlot}-${energyLevel}-${index}`,
        title: suggestion,
        type: 'energy-suggestion',
        category: 'Energy Match',
        energyLevel,
        timeSlot,
        icon: energyLevel === 'low' ? Coffee : energyLevel === 'medium' ? BookOpen : Zap,
        source: 'default'
      });
    });
  }

  return suggestions;
};

export default function UnifiedBig3Planner({ 
  user, 
  date = new Date(), 
  onSave, 
  compact = false,
  isMobile: providedIsMobile,
  breakpoint: providedBreakpoint
}) {
  // Use provided responsive props or fallback to hook
  const hookData = useRoleAwareLayout();
  const isMobile = providedIsMobile !== undefined ? providedIsMobile : hookData.isMobile;
  const breakpoint = providedBreakpoint !== undefined ? providedBreakpoint : hookData.breakpoint;
  const { activities, plans, updatePlan, addActivity, familyCalendar } = useFamilyContext();
  
  // Tomorrow planning state
  const [planningMode, setPlanningMode] = useState('today'); // 'today' or 'tomorrow'
  const [showTomorrowOption, setShowTomorrowOption] = useState(false);
  
  // Core state
  const [selectedEnergy, setSelectedEnergy] = useState('medium');
  const [selectedActivities, setSelectedActivities] = useState({
    intention1: null,
    intention2: null,
    intention3: null
  });
  const [completedActivities, setCompletedActivities] = useState({
    intention1: false,
    intention2: false,
    intention3: false
  });
  
  // Context-aware suggestion state
  const [contextSuggestions, setContextSuggestions] = useState({
    calendar: [],
    todos: [],
    tasks: [],
    energyAware: []
  });
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Tomorrow's state
  const [tomorrowSelectedActivities, setTomorrowSelectedActivities] = useState({
    intention1: null,
    intention2: null,
    intention3: null
  });
  const [tomorrowCompletedActivities, setTomorrowCompletedActivities] = useState({
    intention1: false,
    intention2: false,
    intention3: false
  });
  
  // Core planning state
  const [currentTimeSlot, setCurrentTimeSlot] = useState('intention1');
  const [currentTimeSlotIndex, setCurrentTimeSlotIndex] = useState(0);
  
  // Use normal state setters (debug removed to reduce console noise)
  
  // Desktop expansion state
  const [expandedSlot, setExpandedSlot] = useState(null);
  
  // Prevent edit clicks immediately after activity selection
  const [recentlySelected, setRecentlySelected] = useState(null);
  const recentlySelectedRef = useRef(null);
  
  // Mobile planning screen state
  const [showPlanningScreen, setShowPlanningScreen] = useState(false);
  
  // Planning state
  const [customInput, setCustomInput] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [filteredActivities, setFilteredActivities] = useState([]);
  
  // Real data state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [todoItems, setTodoItems] = useState([]);

  // Load dismissed suggestions on mount
  useEffect(() => {
    if (user?.id) {
      const dismissed = loadDismissedSuggestions(user.id);
      setDismissedSuggestions(dismissed);
    }
  }, [user?.id]);
  
  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const isParent = user?.isParent || false;
  const dateKey = format(date, 'yyyy-MM-dd');
  const userPlans = (plans && user?.id && plans[user.id] && plans[user.id][dateKey]) || {};
  
  // Debug: Only log when plans structure changes significantly (removed to reduce noise)
  
  // Tomorrow planning logic
  const tomorrow = addDays(date, 1);
  const tomorrowDateKey = format(tomorrow, 'yyyy-MM-dd');
  const tomorrowUserPlans = (plans && user?.id && plans[user.id] && plans[user.id][tomorrowDateKey]) || {};
  
  // Check if it's evening (after 6pm) and today to show tomorrow option
  const isEvening = () => {
    const now = new Date();
    const sixPM = setHours(setMinutes(new Date(), 0), 18);
    return isToday(date) && isAfter(now, sixPM);
  };
  
  // Get current planning date and activities based on mode
  const currentPlanningDate = planningMode === 'tomorrow' ? tomorrow : date;
  const currentActivities = planningMode === 'tomorrow' ? tomorrowSelectedActivities : selectedActivities;
  const setCurrentActivities = planningMode === 'tomorrow' ? setTomorrowSelectedActivities : setSelectedActivities;
  const currentCompletedActivities = planningMode === 'tomorrow' ? tomorrowCompletedActivities : completedActivities;
  const setCurrentCompletedActivities = planningMode === 'tomorrow' ? setTomorrowCompletedActivities : setCompletedActivities;

  // Check if should show tomorrow option
  useEffect(() => {
    setShowTomorrowOption(isEvening());
  }, [date]);

  // Load existing Big3 data for today
  useEffect(() => {
    // Check both big3 (from local state) and big_3 (from database)
    const big3Data = userPlans.big3 || userPlans.big_3;
    
    if (big3Data && Array.isArray(big3Data)) {
      console.log('✅ Loading Big3 data:', big3Data);
      const activities = {};
      const completed = {};
      
      big3Data.forEach((item, index) => {
        const slot = TIME_SLOTS[index]?.id || item.timeSlot;
        if (slot && item.text) {
          activities[slot] = {
            type: item.isCustom ? 'custom' : 'activity',
            activity: item.text,
            id: item.activityId
          };
          completed[slot] = item.completed || false;
        }
      });
      
      setSelectedActivities(activities);
      setCompletedActivities(completed);
      
      // Preserve current planning flow by advancing to next empty slot
      const nextSlot = TIME_SLOTS.find(slot => !activities[slot.id])?.id;
      if (nextSlot && nextSlot !== 'intention1') {
        console.log('🔄 Restoring planning flow to next empty slot:', nextSlot);
        setCurrentTimeSlot(nextSlot);
        setCurrentTimeSlotIndex(TIME_SLOTS.findIndex(slot => slot.id === nextSlot));
      }
    }
  }, [userPlans.big3, userPlans.big_3]); // Only depend on the specific properties we care about

  // Load existing Big3 data for tomorrow
  useEffect(() => {
    // Check both big_3 (from database) and big3 (from updates)
    const big3Data = tomorrowUserPlans.big_3 || tomorrowUserPlans.big3;
    if (big3Data) {
      const activities = {};
      big3Data.forEach((item, index) => {
        const slot = TIME_SLOTS[index]?.id || item.timeSlot;
        if (slot && item.text) {
          activities[slot] = {
            type: item.isCustom ? 'custom' : 'activity',
            activity: item.text,
            id: item.activityId
          };
        }
      });
      setTomorrowSelectedActivities(activities);
    }
  }, [tomorrowUserPlans]);

  // Load real calendar events and todos for today
  useEffect(() => {
    const loadTodayData = async () => {
      if (!user?.id) return;
      
      // Load calendar events for today
      const events = getTodayCalendarEvents(familyCalendar, date, user.id);
      setCalendarEvents(events);
      
      // Load todos for today
      const todos = await getTodayTodos(user.id, date);
      setTodoItems(todos);
    };
    
    loadTodayData();
  }, [familyCalendar, user?.id, date]);

  // Auto-save functionality
  const autoSave = useCallback(async (activities, completed = currentCompletedActivities, mode = planningMode) => {
    console.log('🔄 AutoSave called with:', { activities, completed, mode, userId: user?.id });
    
    if (!user?.id) {
      console.warn('❌ AutoSave: No user ID');
      return;
    }
    
    setIsSaving(true);
    try {
      const big3Items = TIME_SLOTS.map(slot => ({
        id: slot.id,
        timeSlot: slot.id,
        text: activities[slot.id]?.activity || '',
        completed: mode === 'today' ? (completed[slot.id] || false) : false, // Tomorrow items start incomplete
        isCustom: activities[slot.id]?.type === 'custom',
        activityId: activities[slot.id]?.id
      }));

      console.log('📝 Big3 items to save:', big3Items);

      const planData = {
        big3: big3Items,
        userType: isParent ? 'parent' : 'kid',
        lastUpdated: new Date().toISOString()
      };
      
      const targetDateKey = mode === 'tomorrow' ? tomorrowDateKey : dateKey;
      console.log('💾 Saving to date:', targetDateKey, 'with data:', planData);
      
      await updatePlan(user.id, targetDateKey, planData);
      setLastSaved(new Date());
      
      console.log('✅ AutoSave successful!');
      
      if (onSave) {
        onSave(planData);
      }
    } catch (error) {
      console.error('❌ Failed to autosave:', error);
    } finally {
      setIsSaving(false);
    }
  }, [user?.id, dateKey, tomorrowDateKey, isParent, updatePlan, onSave]);

  // Filter activities based on energy level and user type
  const getFilteredActivities = useCallback(() => {
    const userActivities = activities[user?.id] || [];
    return userActivities.filter(activity => {
      const energyMatch = activity.energyLevel === selectedEnergy;
      const userTypeMatch = isParent 
        ? ['parent', 'both'].includes(activity.userType)
        : ['kid', 'both'].includes(activity.userType);
      return energyMatch && userTypeMatch;
    });
  }, [activities, user?.id, selectedEnergy, isParent]);

  // Update filtered activities when energy changes
  useEffect(() => {
    setFilteredActivities(getFilteredActivities());
  }, [getFilteredActivities]);

  // Load context-aware suggestions
  const loadContextSuggestions = useCallback(async () => {
    if (!user?.id) return;
    
    setLoadingSuggestions(true);
    try {
      const targetDate = planningMode === 'today' ? date : addDays(date, 1);
      
      // Load calendar events for the day
      const calendarSuggestions = getTodayCalendarEvents(familyCalendar, targetDate, user.id);
      
      // Load personal todos due today
      const todoSuggestions = await getTodayTodos(user.id, targetDate);
      
      // Load smart task suggestions from main task list (excluding Big3-excluded tasks)
      const taskSuggestions = await taskBig3Integration.getTaskSuggestionsForBig3(
        user.id, 
        currentTimeSlot, 
        selectedEnergy
      );
      
      // Generate energy-aware suggestions for current time slot
      const userActivities = activities[user.id] || [];
      const energySuggestions = getEnergyAwareSuggestions(currentTimeSlot, selectedEnergy, userActivities);
      
      setContextSuggestions({
        calendar: calendarSuggestions,
        todos: todoSuggestions,
        tasks: taskSuggestions,
        energyAware: energySuggestions
      });
    } catch (error) {
      console.error('Failed to load context suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [user?.id, date, planningMode, familyCalendar, currentTimeSlot, selectedEnergy, activities]);

  // Load suggestions when relevant dependencies change
  useEffect(() => {
    loadContextSuggestions();
  }, [loadContextSuggestions]);

  // Handle suggestion dismissal
  const handleDismissSuggestion = useCallback((suggestionId) => {
    const newDismissed = new Set(dismissedSuggestions);
    newDismissed.add(suggestionId);
    setDismissedSuggestions(newDismissed);
    
    if (user?.id) {
      saveDismissedSuggestions(user.id, newDismissed);
    }
  }, [dismissedSuggestions, user?.id]);
  
  // Handle excluding tasks from future Big3 suggestions
  const handleExcludeTaskFromBig3 = useCallback(async (taskId) => {
    try {
      // Update the task to exclude it from Big3 suggestions
      await tasksService.updateTask(taskId, { big3_ready: false }, user?.id);
      
      // Remove the task from current suggestions
      setContextSuggestions(prev => ({
        ...prev,
        tasks: prev.tasks.filter(task => task.id !== taskId)
      }));
      
      console.log(`✅ Task ${taskId} excluded from Big3 suggestions`);
    } catch (error) {
      console.error('Failed to exclude task from Big3:', error);
    }
  }, [user?.id]);

  // Get filtered suggestions (excluding dismissed ones)
  const getFilteredSuggestions = useCallback(() => {
    const allSuggestions = [
      ...contextSuggestions.calendar.map(s => ({ ...s, category: 'Today\'s Events', icon: Calendar })),
      ...contextSuggestions.todos.map(s => ({ ...s, category: 'Due Today', icon: ListTodo })),
      ...(contextSuggestions.tasks || []).map(s => ({ ...s, category: 'Smart Tasks', icon: Star })),
      ...contextSuggestions.energyAware
    ];
    
    return allSuggestions.filter(suggestion => !dismissedSuggestions.has(suggestion.id));
  }, [contextSuggestions, dismissedSuggestions]);

  // Handle energy selection
  const handleEnergySelect = (energyLevel) => {
    setSelectedEnergy(energyLevel);
    // Haptic feedback for mobile
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    // Announce to screen readers
    if (window.speechSynthesis && selectedEnergy !== energyLevel) {
      const utterance = new SpeechSynthesisUtterance(`Energy level changed to ${energyLevel}`);
      utterance.volume = 0.1;
      utterance.rate = 1.2;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Handle activity selection
  const handleActivitySelect = (activity, type = 'activity', targetSlot = null) => {
    console.log('🎯 handleActivitySelect called:', { activity, type, targetSlot, currentTimeSlot, customInput });
    
    const slotToUse = targetSlot || currentTimeSlot;
    const activityText = type === 'custom' ? customInput.trim() : activity.name || activity.title;
    
    console.log('📍 Selected activity details:', { slotToUse, activityText, type });
    
    const newActivities = {
      ...currentActivities,
      [slotToUse]: {
        type,
        activity: activityText,
        id: activity.id
      }
    };
    
    console.log('🔄 New activities state:', newActivities);
    
    setCurrentActivities(newActivities);
    
    // Mark this slot as recently selected to prevent edit conflicts
    setRecentlySelected(slotToUse);
    recentlySelectedRef.current = slotToUse;
    
    // Clear input and selection immediately
    setCustomInput('');
    setSelectedSuggestion(null);
    
    // Auto-advance to next empty slot FIRST, before saving
    const nextSlot = findNextEmptySlot(newActivities);
    console.log('🔄 Auto-advance - nextSlot:', nextSlot, 'isMobile:', isMobile);
    
    if (nextSlot) {
      console.log('➡️ Moving to next slot:', nextSlot);
      const nextIndex = TIME_SLOTS.findIndex(slot => slot.id === nextSlot);
      setCurrentTimeSlot(nextSlot);
      setCurrentTimeSlotIndex(nextIndex);
      
      if (isMobile) {
        console.log('📱 Mobile: keeping planning screen open for next slot');
      } else {
        setExpandedSlot(nextSlot);
        setTimeout(() => {
          const input = document.querySelector(`[data-slot="${nextSlot}"] .custom-input`);
          if (input) input.focus();
        }, 100);
      }
    } else {
      console.log('✅ All slots filled');
      if (isMobile) {
        setShowPlanningScreen(false);
      } else {
        setExpandedSlot(null);
      }
    }
    
    // Only auto-save if all slots are filled (to prevent disrupting the flow)
    const allSlotsFilled = TIME_SLOTS.every(slot => newActivities[slot.id]);
    if (allSlotsFilled) {
      console.log('💾 All slots filled - auto-saving');
      setTimeout(() => {
        autoSave(newActivities, currentCompletedActivities, planningMode);
      }, 100);
    } else {
      console.log('📝 Slot filled but not saving yet - waiting for complete planning');
    }
    
    // Clear the recently selected flag after a delay
    setTimeout(() => {
      setRecentlySelected(null);
      recentlySelectedRef.current = null;
    }, 300);
  };

  // Find next empty slot
  const findNextEmptySlot = (activities = currentActivities) => {
    return TIME_SLOTS.find(slot => !activities[slot.id])?.id;
  };

  // Toggle completion status
  const toggleCompletion = (slotId) => {
    const newCompleted = {
      ...currentCompletedActivities,
      [slotId]: !currentCompletedActivities[slotId]
    };
    setCurrentCompletedActivities(newCompleted);
    // Save immediately
    autoSave(currentActivities, newCompleted);
  };

  // Handle editing an existing activity
  const handleEditActivity = (slotId) => {
    const activity = currentActivities[slotId];
    if (!activity) return;
    
    // Prevent edit if this slot was recently selected (to avoid conflicts with auto-advance)
    if (recentlySelectedRef.current === slotId) {
      console.log('🚫 Prevented edit on recently selected slot:', slotId);
      return;
    }
    
    // Always update current time slot
    setCurrentTimeSlot(slotId);
    setCurrentTimeSlotIndex(TIME_SLOTS.findIndex(slot => slot.id === slotId));
    
    setCustomInput(activity.activity);
    
    if (isMobile) {
      // Mobile: Show full-screen planning
      setShowPlanningScreen(true);
    } else {
      // Desktop: Inline expansion
      setExpandedSlot(slotId);
      // Focus input after expansion
      setTimeout(() => {
        const input = document.querySelector(`[data-slot="${slotId}"] .custom-input`);
        if (input) {
          input.focus();
          input.select(); // Select all text for easy editing
        }
      }, 100);
    }
  };

  // Handle slot click
  const handleSlotClick = (slotId) => {
    // Always update current time slot
    setCurrentTimeSlot(slotId);
    setCurrentTimeSlotIndex(TIME_SLOTS.findIndex(slot => slot.id === slotId));
    
    if (isMobile) {
      // Mobile: Show full-screen planning
      setShowPlanningScreen(true);
    } else {
      // Desktop: Inline expansion
      setExpandedSlot(expandedSlot === slotId ? null : slotId);
      if (expandedSlot !== slotId) {
        // Focus input after expansion
        setTimeout(() => {
          const input = document.querySelector(`[data-slot="${slotId}"] .custom-input`);
          if (input) input.focus();
        }, 100);
      }
    }
  };

  // Completion status
  const completedCount = Object.values(currentActivities).filter(Boolean).length;
  const actuallyCompletedCount = Object.values(currentCompletedActivities).filter(Boolean).length;
  const isComplete = completedCount === 3;
  
  // Day mode toggle
  const handleDayModeToggle = (mode) => {
    setPlanningMode(mode);
          setCurrentTimeSlot('intention1');
    setCurrentTimeSlotIndex(0);
    setExpandedSlot(null);
    setCustomInput('');
    setSelectedSuggestion(null);
  };

  // Save to library
  const saveToLibrary = async (activityName) => {
    try {
      await addActivity(user.id, {
        name: activityName,
        category: 'other',
        energyLevel: selectedEnergy,
        isCustom: true,
        addedBy: user?.name || 'user'
      });
    } catch (error) {
      console.error('Error saving to library:', error);
    }
  };

  // Compact mode for mobile home screen
  if (compact) {
    return (
      <DashboardScreen 
        selectedEnergy={selectedEnergy}
        onEnergySelect={handleEnergySelect}
        selectedActivities={currentActivities}
        completedActivities={currentCompletedActivities}
        onSlotClick={handleSlotClick}
        onEditActivity={handleEditActivity}
        onToggleCompletion={toggleCompletion}
        completedCount={completedCount}
        isSaving={isSaving}
        lastSaved={lastSaved}
        expandedSlot={null} // Always collapsed in compact mode
        setExpandedSlot={() => {}} // No expansion in compact mode
        customInput={customInput}
        setCustomInput={setCustomInput}
        selectedSuggestion={selectedSuggestion}
        setSelectedSuggestion={setSelectedSuggestion}
        filteredActivities={filteredActivities}
        calendarEvents={calendarEvents}
        todoItems={todoItems}
        onActivitySelect={handleActivitySelect}
        isDesktop={false}
        compact={true}
        showTomorrowOption={showTomorrowOption}
        planningMode={planningMode}
        onDayModeToggle={handleDayModeToggle}
        currentPlanningDate={currentPlanningDate}
        contextSuggestions={contextSuggestions}
        handleDismissSuggestion={handleDismissSuggestion}
        handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
        loadingSuggestions={loadingSuggestions}
      />
    );
  }

  // Mobile: Show planning screen when active
  if (isMobile && showPlanningScreen) {
    console.log('🎯 Main component rendering mobile planning screen:', {
      currentTimeSlot,
      currentTimeSlotIndex,
      showPlanningScreen
    });
    
    return (
      <PlanningScreen
        currentTimeSlot={currentTimeSlot}
        currentTimeSlotIndex={currentTimeSlotIndex}
        customInput={customInput}
        setCustomInput={setCustomInput}
        selectedSuggestion={selectedSuggestion}
        setSelectedSuggestion={setSelectedSuggestion}
        filteredActivities={filteredActivities}
        calendarEvents={calendarEvents}
        todoItems={todoItems}
        onActivitySelect={handleActivitySelect}
        onBack={() => {
          // Save current progress before closing
          console.log('💾 Saving progress before closing planning screen');
          autoSave(currentActivities, currentCompletedActivities, planningMode);
          
          setShowPlanningScreen(false);
          setCustomInput('');
          setSelectedSuggestion(null);
        }}
        completedCount={completedCount}
        isSaving={isSaving}
        contextSuggestions={contextSuggestions}
        handleDismissSuggestion={handleDismissSuggestion}
        handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
        loadingSuggestions={loadingSuggestions}
      />
    );
  }

  // Use the same layout for both mobile and desktop, just with responsive styling
  return (
    <DashboardScreen 
        selectedEnergy={selectedEnergy}
        onEnergySelect={handleEnergySelect}
        selectedActivities={currentActivities}
        completedActivities={currentCompletedActivities}
        onSlotClick={handleSlotClick}
        onEditActivity={handleEditActivity}
        onToggleCompletion={toggleCompletion}
        completedCount={completedCount}
        isSaving={isSaving}
        lastSaved={lastSaved}
        expandedSlot={expandedSlot}
        setExpandedSlot={setExpandedSlot}
        customInput={customInput}
        setCustomInput={setCustomInput}
        selectedSuggestion={selectedSuggestion}
        setSelectedSuggestion={setSelectedSuggestion}
        filteredActivities={filteredActivities}
        calendarEvents={calendarEvents}
        todoItems={todoItems}
        onActivitySelect={handleActivitySelect}
        isDesktop={!isMobile}
        showTomorrowOption={showTomorrowOption}
        planningMode={planningMode}
        onDayModeToggle={handleDayModeToggle}
        currentPlanningDate={currentPlanningDate}
        contextSuggestions={contextSuggestions}
        handleDismissSuggestion={handleDismissSuggestion}
        handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
        loadingSuggestions={loadingSuggestions}
      />
  );
}

// Dashboard Screen Component
function DashboardScreen({ 
  selectedEnergy, 
  onEnergySelect, 
  selectedActivities, 
  completedActivities,
  onSlotClick, 
  onEditActivity,
  onToggleCompletion,
  completedCount,
  isSaving,
  expandedSlot,
  setExpandedSlot,
  customInput,
  setCustomInput,
  selectedSuggestion,
  setSelectedSuggestion,
  filteredActivities,
  calendarEvents,
  todoItems,
  onActivitySelect,
  isDesktop = false,
  compact = false,
  showTomorrowOption = false,
  planningMode = 'today',
  onDayModeToggle,
  currentPlanningDate,
  contextSuggestions,
  handleDismissSuggestion,
  handleExcludeTaskFromBig3,
  loadingSuggestions
}) {
  return (
    <div className={compact ? "space-y-3" : "space-y-4 p-4 md:p-0"}>
      {/* Day Selector - Only show when evening option is available and not compact */}
      {showTomorrowOption && !compact && (
        <DaySelector
          planningMode={planningMode}
          onDayModeToggle={onDayModeToggle}
          currentPlanningDate={currentPlanningDate}
          isDesktop={isDesktop}
        />
      )}

      {/* Energy Selector - Hidden in compact mode */}
      {!compact && (
        <EnergySelector 
          selectedEnergy={selectedEnergy} 
          onEnergySelect={onEnergySelect}
          isDesktop={isDesktop}
          isSaving={isSaving}
        />
      )}

      {/* Time Slots */}
      <div className={compact ? "space-y-2" : "space-y-3"}>
        {TIME_SLOTS.map((slot) => (
          <TimeSlotCard
            key={slot.id}
            slot={slot}
            selectedActivity={selectedActivities[slot.id]}
            isCompleted={completedActivities[slot.id]}
            onSlotClick={onSlotClick}
            onEditActivity={onEditActivity}
            onToggleCompletion={onToggleCompletion}
            isExpanded={expandedSlot === slot.id}
            customInput={customInput}
            setCustomInput={setCustomInput}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            filteredActivities={filteredActivities}
            calendarEvents={calendarEvents}
            todoItems={todoItems}
            onActivitySelect={onActivitySelect}
            onCancel={() => setExpandedSlot && setExpandedSlot(null)}
            isDesktop={isDesktop}
            compact={compact}
            contextSuggestions={contextSuggestions}
            handleDismissSuggestion={handleDismissSuggestion}
            handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
            loadingSuggestions={loadingSuggestions}
          />
        ))}
      </div>
    </div>
  );
}

// Energy Selector Component
function EnergySelector({ selectedEnergy, onEnergySelect, isDesktop = false, isSaving = false }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-stone-200">
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            What's your energy like today?
          </h2>
          {isDesktop && isSaving && (
            <div className="flex items-center space-x-1 text-xs text-stone-500">
              <div className="w-2.5 h-2.5 border-2 border-stone-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Saving...</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 md:gap-3">
        {ENERGY_LEVELS.map((energy) => {
          const Icon = energy.icon;
          const isSelected = selectedEnergy === energy.id;
          
          return (
            <button
              key={energy.id}
              onClick={() => onEnergySelect(energy.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEnergySelect(energy.id);
                }
              }}
              className={`
                p-3 rounded-xl border-2 transition-all duration-200 flex flex-col items-center space-y-1 md:space-y-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                ${isSelected 
                  ? `${energy.borderColor} ${energy.bgColor} scale-105 border-3` 
                  : `border-stone-200 bg-white ${energy.hoverBg} hover:border-stone-300`
                }
              `}
              aria-pressed={isSelected}
              aria-label={`${energy.label} energy level${isSelected ? ' - selected' : ''}`}
            >
              <Icon className={`w-5 h-5 md:w-6 md:h-6 ${isSelected ? energy.color : 'text-stone-400'}`} />
              <span className={`text-xs font-semibold ${
                isSelected ? 'text-slate-900' : 'text-stone-600'
              }`}>
                {energy.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Time Slot Card Component
function TimeSlotCard({ 
  slot, 
  selectedActivity, 
  isCompleted,
  onSlotClick, 
  onEditActivity,
  onToggleCompletion,
  isExpanded, 
  customInput, 
  setCustomInput, 
  selectedSuggestion, 
  setSelectedSuggestion, 
  filteredActivities, 
  calendarEvents,
  todoItems,
  onActivitySelect, 
  onCancel,
  isDesktop,
  compact = false,
  contextSuggestions,
  handleDismissSuggestion,
  handleExcludeTaskFromBig3,
  loadingSuggestions
}) {
  const Icon = slot.icon;
  const hasActivity = !!selectedActivity;

  return (
    <div className={`
      ${compact
        ? `bg-stone-50 rounded-lg border border-stone-200 transition-all duration-200 ${
            hasActivity ? 'border-teal-200 bg-teal-50/50' : 'hover:border-stone-300'
          }`
        : isDesktop 
        ? `bg-white rounded-lg border transition-all duration-300 cursor-pointer ${
            hasActivity ? 'border-primary-200 bg-primary-50/30' : 'border-stone-200 hover:border-stone-300'
          } ${isExpanded ? 'border-primary-300 shadow-md' : ''}`
        : `bg-white rounded-2xl border border-stone-200 transition-all duration-300 cursor-pointer ${
            hasActivity ? 'border-primary-500 shadow-lg shadow-primary-500/10' : 'hover:shadow-md hover:-translate-y-1'
          } ${isExpanded ? 'shadow-xl shadow-slate-900/15' : ''}`
      }
    `}>
      {/* Card Header */}
      <div className={compact ? "p-3 flex items-center space-x-3" : isDesktop ? "p-3 flex items-center space-x-3" : "p-5 flex items-center space-x-4"}>
        {/* Checkbox */}
        {hasActivity && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCompletion(slot.id);
            }}
            className={`
              ${compact ? 'w-4 h-4' : isDesktop ? 'w-5 h-5' : 'w-6 h-6'} rounded-md border-2 flex items-center justify-center transition-all
              ${isCompleted 
                ? 'bg-primary-500 border-primary-500 text-white' 
                : 'border-stone-300 hover:border-primary-400 bg-white'
              }
            `}
            aria-label={`Mark ${slot.label.toLowerCase()} as ${isCompleted ? 'incomplete' : 'complete'}`}
          >
            {isCompleted && <Check className={compact ? "w-2.5 h-2.5" : isDesktop ? "w-3 h-3" : "w-4 h-4"} />}
          </button>
        )}
        
        <div 
          className={`
            ${compact ? 'w-8 h-8' : isDesktop ? 'w-9 h-9' : 'w-11 h-11'} rounded-xl flex items-center justify-center text-white
            ${slot.gradient}
          `}
        >
          <Icon className={compact ? "w-4 h-4" : isDesktop ? "w-5 h-5" : "w-6 h-6"} />
        </div>
        
        <div 
          className={`flex-1 ${!compact && !hasActivity ? 'cursor-pointer' : ''}`}
          onClick={() => {
            if (!compact && !isExpanded) {
              if (hasActivity) {
                onEditActivity(slot.id);
              } else {
                onSlotClick(slot.id);
              }
            }
          }}
        >
          <div className={compact ? "text-sm font-semibold text-slate-900" : isDesktop ? "text-base font-semibold text-slate-900" : "text-lg font-semibold text-slate-900"}>
            {slot.label}
          </div>
          <div className={`
            ${compact ? "text-xs" : isDesktop ? "text-sm" : "text-sm"} 
            ${hasActivity 
              ? `${!compact ? 'cursor-pointer hover:text-primary-600' : ''} transition-colors ${isCompleted ? 'line-through text-stone-500' : 'text-stone-900'}` 
              : `text-stone-600 ${!compact ? 'cursor-pointer hover:text-primary-600' : ''} transition-colors`
            }
          `}>
            {hasActivity ? selectedActivity.activity : 'Tap to plan'}
          </div>
        </div>
        
        {!hasActivity && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onSlotClick(slot.id);
            }}
            onTouchStart={(e) => {
              // Prevent double-tap zoom on mobile
              e.preventDefault();
            }}
            className={`${isDesktop ? 'w-7 h-7' : 'w-8 h-8'} bg-primary-500 text-white rounded-full flex items-center justify-center hover:bg-primary-600 active:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 touch-manipulation`}
            aria-label={`Plan activity for ${slot.label.toLowerCase()}`}
          >
            <Plus className={isDesktop ? "w-3.5 h-3.5" : "w-4 h-4"} />
          </button>
        )}
      </div>

      {/* Desktop Expansion */}
      {isDesktop && isExpanded && (
        <div className="border-t border-stone-200 bg-stone-50 transition-all duration-400 overflow-hidden">
          <div className="p-4">
            {/* Expansion Header */}
            <div className="text-center mb-4">
              <div className={`
                inline-block px-4 py-1.5 rounded-full text-xs font-semibold text-white mb-2
                ${slot.gradient}
              `}>
                {slot.label}
              </div>
              <div className="text-base font-semibold text-slate-900">
                What would you like to do?
              </div>
            </div>

            {/* Planning Content */}
            <PlanningContent
              customInput={customInput}
              setCustomInput={setCustomInput}
              selectedSuggestion={selectedSuggestion}
              setSelectedSuggestion={setSelectedSuggestion}
              filteredActivities={filteredActivities}
              calendarEvents={calendarEvents}
              todoItems={todoItems}
              onActivitySelect={(activity, type) => onActivitySelect(activity, type, slot.id)}
              slot={slot.id}
              currentTimeSlot={slot.id}
              contextSuggestions={contextSuggestions}
              handleDismissSuggestion={handleDismissSuggestion}
              handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
              loadingSuggestions={loadingSuggestions}
            />

            {/* Actions - Desktop: just cancel, mobile: continue + cancel */}
            <div className="flex space-x-3 mt-4">
              <button
                onClick={onCancel}
                className="bg-stone-100 text-stone-600 py-2 px-3 rounded-lg text-sm font-medium hover:bg-stone-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Planning Screen Component (Mobile)
function PlanningScreen({ 
  currentTimeSlot, 
  currentTimeSlotIndex, 
  customInput, 
  setCustomInput, 
  selectedSuggestion, 
  setSelectedSuggestion, 
  filteredActivities, 
  calendarEvents,
  todoItems,
  onActivitySelect, 
  onBack, 
  completedCount,
  isSaving,
  contextSuggestions,
  handleDismissSuggestion,
  handleExcludeTaskFromBig3,
  loadingSuggestions
}) {
  const currentSlot = TIME_SLOTS.find(slot => slot.id === currentTimeSlot) || TIME_SLOTS[0];
  const isLastSlot = currentTimeSlotIndex === TIME_SLOTS.length - 1;
  
  console.log('📱 PlanningScreen render:', { 
    currentTimeSlot, 
    currentTimeSlotIndex, 
    currentSlot: currentSlot?.label,
    currentSlotId: currentSlot?.id,
    customInput,
    allTimeSlots: TIME_SLOTS.map(s => s.id),
    expectedSlot: TIME_SLOTS[currentTimeSlotIndex]?.id
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-stone-200 flex items-center justify-between">
        <button onClick={onBack} className="w-10 h-10 bg-stone-100 text-stone-600 rounded-full flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex-1 text-center mx-3">
          <h1 className="text-lg font-bold text-slate-900">Big 3 for Today</h1>
          <div className="text-xs text-stone-500 font-medium">
            {completedCount}/3 complete
          </div>
        </div>
        
        <button 
          className={`px-3 py-2 rounded-full text-xs font-semibold transition-all ${
            completedCount === 3 
              ? 'bg-primary-500 text-white' 
              : 'bg-stone-300 text-stone-500 cursor-not-allowed'
          }`}
          disabled={completedCount < 3}
        >
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        {/* Modal Header */}
        <div className="p-5 text-center">
          <div className={`
            inline-block px-5 py-2 rounded-full text-sm font-semibold text-white mb-4
            ${currentSlot.gradient}
          `}>
            {currentSlot.label}
          </div>
          <div className="text-xl font-semibold text-slate-900">
            What would you like to do?
          </div>
        </div>

        {/* Planning Content */}
        <div className="flex-1 px-5 overflow-y-auto">
          <PlanningContent
            customInput={customInput}
            setCustomInput={setCustomInput}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            filteredActivities={filteredActivities}
            calendarEvents={calendarEvents}
            todoItems={todoItems}
            onActivitySelect={onActivitySelect}
            slot={currentTimeSlot}
            currentTimeSlot={currentTimeSlot}
            contextSuggestions={contextSuggestions}
            handleDismissSuggestion={handleDismissSuggestion}
            handleExcludeTaskFromBig3={handleExcludeTaskFromBig3}
            loadingSuggestions={loadingSuggestions}
          />
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-stone-200 bg-white">
          <button
            onClick={() => {
              if (selectedSuggestion) {
                onActivitySelect(selectedSuggestion, 'activity');
              } else if (customInput.trim()) {
                onActivitySelect({ title: customInput.trim(), name: customInput.trim() }, 'custom');
              }
            }}
            disabled={!selectedSuggestion && !customInput.trim()}
            className="w-full bg-primary-500 text-white py-4 px-6 rounded-xl text-base font-semibold hover:bg-primary-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLastSlot ? 'Complete Planning' : `Next: ${TIME_SLOTS[currentTimeSlotIndex + 1]?.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Planning Content Component (Shared)
function PlanningContent({ 
  customInput, 
  setCustomInput, 
  selectedSuggestion, 
  setSelectedSuggestion, 
  filteredActivities, 
  calendarEvents,
  todoItems,
  onActivitySelect,
  slot,
  currentTimeSlot,
  contextSuggestions,
  handleDismissSuggestion,
  handleExcludeTaskFromBig3,
  loadingSuggestions
}) {
  // Use a ref to manage autofocus
  const inputRef = useRef(null);
  
  // Autofocus when component mounts or slot changes
  useEffect(() => {
    console.log('🔄 PlanningContent effect - focusing input for slot:', slot, 'currentTimeSlot:', currentTimeSlot);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [slot, currentTimeSlot]);
  return (
    <div className="space-y-4">
      {/* Custom Input */}
      <div className="bg-white border-2 border-primary-500 rounded-lg p-3">
        <input
          ref={inputRef}
          type="text"
          className="w-full text-sm bg-transparent border-none outline-none placeholder-stone-400 custom-input"
          placeholder="Type your own activity..."
          value={customInput}
          onChange={(e) => {
            setCustomInput(e.target.value);
            if (e.target.value.trim()) {
              setSelectedSuggestion(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (selectedSuggestion) {
                onActivitySelect(selectedSuggestion, 'activity');
              } else if (customInput.trim()) {
                onActivitySelect({ title: customInput.trim(), name: customInput.trim() }, 'custom');
              }
            }
          }}
          maxLength={50}
          data-slot={slot}
          autoFocus
        />
      </div>

      {/* Suggestions Container */}
      <div className="space-y-3 max-h-60 overflow-y-auto">
        {/* Context-Aware Suggestions */}
        {contextSuggestions.calendar.length > 0 && (
          <SuggestionSection
            title="Today's Events"
            items={contextSuggestions.calendar}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="calendar"
            iconComponent={Calendar}
            iconBg="bg-magenta-500"
          />
        )}

        {contextSuggestions.todos.length > 0 && (
          <SuggestionSection
            title="Due Today"
            items={contextSuggestions.todos}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="todo"
            iconComponent={ListTodo}
            iconBg="bg-azure-500"
          />
        )}

        {contextSuggestions.tasks && contextSuggestions.tasks.length > 0 && (
          <SuggestionSection
            title="Smart Tasks"
            items={contextSuggestions.tasks}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="task"
            iconComponent={Star}
            iconBg="bg-violet-500"
            onDismiss={handleExcludeTaskFromBig3}
            showDismiss={true}
          />
        )}

        {contextSuggestions.energyAware.length > 0 && (
          <SuggestionSection
            title="Energy Match"
            items={contextSuggestions.energyAware}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="energy-suggestion"
            iconComponent={Zap}
            iconBg="bg-coral-500"
            onDismiss={handleDismissSuggestion}
            showDismiss={true}
          />
        )}

        {/* Your Activities */}
        <SuggestionSection
          title="Your Activities"
          items={filteredActivities}
          selectedSuggestion={selectedSuggestion}
          setSelectedSuggestion={setSelectedSuggestion}
          setCustomInput={setCustomInput}
          onSelect={onActivitySelect}
          type="activity"
          iconComponent={Target}
          iconBg="bg-primary-500"
        />

        {/* Legacy fallback sections (hidden if context suggestions are available) */}
        {contextSuggestions.calendar.length === 0 && calendarEvents.length > 0 && (
          <SuggestionSection
            title="Calendar Events"
            items={calendarEvents}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="calendar"
            iconComponent={Calendar}
            iconBg="bg-magenta-500"
          />
        )}

        {contextSuggestions.todos.length === 0 && todoItems.length > 0 && (
          <SuggestionSection
            title="To-Do Items"
            items={todoItems}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
            setCustomInput={setCustomInput}
            onSelect={onActivitySelect}
            type="todo"
            iconComponent={Mail}
            iconBg="bg-gold-500"
          />
        )}

        {loadingSuggestions && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-xs text-stone-500">Loading suggestions...</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Suggestion Section Component
function SuggestionSection({ 
  title, 
  items, 
  selectedSuggestion, 
  setSelectedSuggestion, 
  setCustomInput,
  onSelect, 
  type, 
  iconComponent: Icon, 
  iconBg,
  onDismiss = null,
  showDismiss = false
}) {
  if (!items || items.length === 0) return null;

  return (
    <div>
      <div className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
        {title}
      </div>
      <div className="space-y-1">
        {items.map((item) => (
          <div
            key={item.id}
            className={`
              relative w-full bg-white border rounded-lg transition-all
              ${selectedSuggestion?.id === item.id 
                ? 'border-primary-500 bg-primary-50 shadow-sm' 
                : 'border-stone-200 hover:border-primary-300 hover:bg-stone-50'
              }
            `}
          >
            <button
              onClick={() => {
                // Populate the text field with the suggestion
                const activityText = item.name || item.title;
                setCustomInput(activityText);
                setSelectedSuggestion(item);
                // Haptic feedback
                if (navigator.vibrate) {
                  navigator.vibrate(50);
                }
              }}
              className="w-full p-2 flex items-center space-x-2 text-left"
            >
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0 ${iconBg}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {item.name || item.title}
                </div>
                <div className="text-xs text-stone-500 truncate">
                  {item.time ? `${item.time} • ${item.category || 'Event'}` : 
                   item.isOverdue ? `Overdue • ${item.category}` :
                   item.dueDate ? `Due today • ${item.category}` :
                   item.category}
                </div>
              </div>
              {(item.priority === 'high' || item.isOverdue) && (
                <div className="flex-shrink-0">
                  <Star className={`w-3 h-3 ${item.isOverdue ? 'text-red-500' : 'text-amber-500'} fill-current`} />
                </div>
              )}
            </button>
            
            {showDismiss && onDismiss && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(item.id);
                }}
                className="absolute top-1 right-1 p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                title="Don't suggest again"
              >
                <EyeOff className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Completed Screen Component (Mobile)
function CompletedScreen({ selectedActivities, onBack, onSaveToLibrary, isSaving }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-stone-200 flex items-center justify-between">
        <button onClick={onBack} className="w-10 h-10 bg-stone-100 text-stone-600 rounded-full flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex-1 text-center mx-3">
          <h1 className="text-lg font-bold text-slate-900">Big 3 for Today</h1>
          <div className="text-xs text-stone-500 font-medium">
            3/3 complete
          </div>
        </div>
        
        <button className="bg-primary-500 text-white px-3 py-2 rounded-full text-xs font-semibold">
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-5 space-y-4">
        {TIME_SLOTS.map((slot) => {
          const activity = selectedActivities[slot.id];
          if (!activity) return null;

          return (
            <div key={slot.id} className="bg-white rounded-2xl border border-stone-200 p-5 flex items-center space-x-4">
              <div className="w-6 h-6 bg-primary-500 text-white rounded-lg flex items-center justify-center">
                <CheckCircle className="w-4 h-4" />
              </div>
              
              <div className="flex-1">
                <div className="text-base font-semibold text-slate-900">
                  {activity.activity}
                </div>
                <div className="text-sm text-stone-500">
                  {slot.label}
                </div>
              </div>
              
              {activity.type === 'custom' && (
                <button
                  onClick={() => onSaveToLibrary(activity.activity)}
                  className="bg-stone-100 text-stone-600 px-3 py-2 rounded-lg text-xs font-medium hover:bg-stone-200 transition-colors flex items-center space-x-1"
                >
                  <BookmarkPlus className="w-3 h-3" />
                  <span>Save to Library</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Day Selector Component
function DaySelector({ planningMode, onDayModeToggle, currentPlanningDate, isDesktop = false }) {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  
  const formatDateLabel = (date) => {
    if (isToday(date)) return 'Today';
    return format(date, 'MMM d');
  };

  return (
    <div className={`${
      isDesktop 
        ? "bg-white rounded-lg border border-stone-200 p-3" 
        : "bg-white mx-5 rounded-2xl p-4 border border-stone-200"
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-stone-500" />
          <span className={`font-semibold ${isDesktop ? 'text-sm' : 'text-base'} text-slate-900`}>
            Planning Day
          </span>
        </div>
        <div className="text-xs text-stone-500">
          Get a head start on tomorrow after 6pm!
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onDayModeToggle('today')}
          className={`
            ${isDesktop ? 'p-2' : 'p-3'} rounded-lg border-2 transition-all duration-200 flex flex-col items-center ${isDesktop ? 'space-y-1' : 'space-y-2'} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${planningMode === 'today'
              ? 'border-primary-500 bg-primary-50 text-primary-700' 
              : 'border-stone-200 bg-white hover:border-stone-300 text-stone-600'
            }
          `}
        >
          <Sun className={`${isDesktop ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <div>
            <div className={`${isDesktop ? 'text-xs' : 'text-sm'} font-semibold`}>
              {formatDateLabel(today)}
            </div>
            <div className={`${isDesktop ? 'text-xs' : 'text-xs'} opacity-75`}>
              Today's Big 3
            </div>
          </div>
        </button>
        
        <button
          onClick={() => onDayModeToggle('tomorrow')}
          className={`
            ${isDesktop ? 'p-2' : 'p-3'} rounded-lg border-2 transition-all duration-200 flex flex-col items-center ${isDesktop ? 'space-y-1' : 'space-y-2'} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${planningMode === 'tomorrow'
              ? 'border-primary-500 bg-primary-50 text-primary-700' 
              : 'border-stone-200 bg-white hover:border-stone-300 text-stone-600'
            }
          `}
        >
          <MoonIcon className={`${isDesktop ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <div>
            <div className={`${isDesktop ? 'text-xs' : 'text-sm'} font-semibold`}>
              {formatDateLabel(tomorrow)}
            </div>
            <div className={`${isDesktop ? 'text-xs' : 'text-xs'} opacity-75`}>
              Tomorrow's Big 3
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}